import { useEffect, useState } from "react";
import { MagnifyingGlass, Plus, PencilSimple, Trash, XCircle } from "@phosphor-icons/react";
import { listProducts, listSuppliers, createProduct, updateProduct, deleteProduct } from "@/lib/api";
import { toast } from "sonner";

export default function ProductPicker({ onPick, onClose, selectedId, allowEdit = true }) {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // product obj or 'new'
  const [form, setForm] = useState({});

  const load = async () => {
    const [p, s] = await Promise.all([listProducts({ q }), listSuppliers()]);
    setProducts(p);
    setSuppliers(s);
  };
  useEffect(() => { load(); }, [q]); // eslint-disable-line

  const openNew = () => {
    setForm({ name: "", category: "Général", unit: "unité", stock: 0, min_threshold: 10, unit_price: 0, pack_size: 1, supplier_id: "" });
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({ ...p, supplier_id: p.supplier_id || "" });
    setEditing(p);
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.supplier_id) payload.supplier_id = null;
      payload.stock = Number(payload.stock) || 0;
      payload.min_threshold = Number(payload.min_threshold) || 0;
      payload.unit_price = Number(payload.unit_price) || 0;
      payload.pack_size = Number(payload.pack_size) || 1;
      if (editing === "new") {
        await createProduct(payload);
        toast.success("Produit créé");
      } else {
        await updateProduct(editing.id, payload);
        toast.success("Produit mis à jour");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    await deleteProduct(p.id);
    toast.success("Supprimé");
    load();
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="product-picker">
      <div className="bg-white w-full max-w-3xl rounded-t-3xl md:rounded-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--border-soft)]">
          <div>
            <div className="font-heading text-xl font-semibold">Choisir un produit</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-0.5">Tapez pour rechercher</div>
          </div>
          <button onClick={onClose} className="w-12 h-12 rounded-2xl hover:bg-[color:var(--brand-muted)] flex items-center justify-center" data-testid="picker-close">
            <XCircle size={28} />
          </button>
        </div>

        <div className="p-5 border-b border-[color:var(--border-soft)] flex gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
            <input
              data-testid="picker-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un produit…"
              className="w-full h-14 pl-12 pr-4 rounded-2xl border border-[color:var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
            />
          </div>
          {allowEdit && (
            <button
              onClick={openNew}
              data-testid="picker-new"
              className="h-14 px-5 bg-[color:var(--brand)] text-white rounded-2xl flex items-center gap-2 press-scale hover:bg-[color:var(--brand-hover)]"
            >
              <Plus size={24} weight="bold" /> Nouveau
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {products.length === 0 ? (
            <div className="text-center py-16 text-[color:var(--text-muted)]">
              Aucun produit. Créez-en un pour commencer.
            </div>
          ) : (
            <ul>
              {products.map((p) => (
                <li
                  key={p.id}
                  className={`flex items-center gap-3 px-5 py-4 border-b border-[color:var(--border-soft)] hover:bg-[color:var(--bg)] cursor-pointer ${selectedId === p.id ? "bg-[color:var(--brand-muted)]" : ""}`}
                  onClick={() => onPick(p)}
                  data-testid={`picker-item-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[color:var(--text-primary)] truncate">{p.name}</div>
                    <div className="text-sm text-[color:var(--text-muted)] flex gap-3 mt-0.5">
                      <span>{p.category}</span>
                      <span>·</span>
                      <span>Stock : <b>{p.stock}</b> {p.unit}</span>
                      {p.supplier_name && <><span>·</span><span>{p.supplier_name}</span></>}
                    </div>
                  </div>
                  {allowEdit && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="w-11 h-11 rounded-xl hover:bg-[color:var(--brand-muted)] flex items-center justify-center" data-testid={`picker-edit-${p.id}`}>
                        <PencilSimple size={20} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); remove(p); }} className="w-11 h-11 rounded-xl hover:bg-red-50 text-red-700 flex items-center justify-center" data-testid={`picker-delete-${p.id}`}>
                        <Trash size={20} />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {editing && (
        <ProductForm
          form={form}
          setForm={setForm}
          suppliers={suppliers}
          onSave={save}
          onClose={() => setEditing(null)}
          isNew={editing === "new"}
        />
      )}
    </div>
  );
}

function ProductForm({ form, setForm, suppliers, onSave, onClose, isNew }) {
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="product-form">
      <div className="bg-white w-full max-w-xl rounded-3xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-xl font-semibold">{isNew ? "Nouveau produit" : "Modifier le produit"}</div>
          <button onClick={onClose} className="w-11 h-11 rounded-xl hover:bg-[color:var(--brand-muted)]"><XCircle size={24} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nom*" className="md:col-span-2">
            <input data-testid="form-name" value={form.name || ""} onChange={(e) => upd("name", e.target.value)} className="input" />
          </Field>
          <Field label="Catégorie">
            <input data-testid="form-category" value={form.category || ""} onChange={(e) => upd("category", e.target.value)} className="input" />
          </Field>
          <Field label="Unité">
            <input data-testid="form-unit" value={form.unit || ""} onChange={(e) => upd("unit", e.target.value)} placeholder="boîte, paire, unité…" className="input" />
          </Field>
          <Field label="Stock actuel">
            <input data-testid="form-stock" type="number" value={form.stock ?? 0} onChange={(e) => upd("stock", e.target.value)} className="input" />
          </Field>
          <Field label="Seuil minimum">
            <input data-testid="form-threshold" type="number" value={form.min_threshold ?? 0} onChange={(e) => upd("min_threshold", e.target.value)} className="input" />
          </Field>
          <Field label="Prix unitaire (€)">
            <input data-testid="form-price" type="number" step="0.01" value={form.unit_price ?? 0} onChange={(e) => upd("unit_price", e.target.value)} className="input" />
          </Field>
          <Field label="Conditionnement">
            <input data-testid="form-pack" type="number" value={form.pack_size ?? 1} onChange={(e) => upd("pack_size", e.target.value)} className="input" />
          </Field>
          <Field label="Fournisseur" className="md:col-span-2">
            <select data-testid="form-supplier" value={form.supplier_id || ""} onChange={(e) => upd("supplier_id", e.target.value)} className="input">
              <option value="">— Aucun —</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Notes" className="md:col-span-2">
            <textarea data-testid="form-notes" value={form.notes || ""} onChange={(e) => upd("notes", e.target.value)} className="input min-h-[80px]" rows={2} />
          </Field>
          <label className="md:col-span-2 flex items-center gap-3 text-sm text-[color:var(--text-secondary)]">
            <input type="checkbox" checked={form.auto_threshold !== false} onChange={(e) => upd("auto_threshold", e.target.checked)} className="w-5 h-5 accent-[color:var(--brand)]" />
            Ajuster automatiquement le seuil minimum selon l'historique
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="h-14 px-5 rounded-2xl border border-[color:var(--border-soft)] press-scale" data-testid="form-cancel">Annuler</button>
          <button onClick={onSave} className="h-14 px-6 bg-[color:var(--brand)] text-white rounded-2xl press-scale hover:bg-[color:var(--brand-hover)]" data-testid="form-save">
            {isNew ? "Créer" : "Enregistrer"}
          </button>
        </div>
      </div>
      <style>{`.input{width:100%;height:56px;padding:0 16px;border-radius:14px;border:1px solid var(--border-soft);background:white;font-size:15px;outline:none}.input:focus{box-shadow:0 0 0 2px var(--brand)}textarea.input{padding:12px 16px;height:auto}`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)] block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
